export type MentionAttrs = {
	id: string;
	label: string;
};

const mergeFieldToken = /%[A-Za-z][A-Za-z0-9_.() -]*%/g;
const urlEncodedByteToken = /^%[0-9A-Fa-f]{2}%$/;
const imageSourceKeys = new Set(['imageUrl', 'imageSrc', 'imgSrc']);
const mentionSpan = /<span\b(?=[^>]*\bdata-id\s*=\s*(['"])(.*?)\1)[^>]*>/gi;
const attribute = /\b([\w-]+)\s*=\s*(['"])(.*?)\2/g;
const systemGeneratedLinkTokens = new Set(['%UnsubscribeEmailAddressLink%']);

type VisitContext = {
	key?: string;
	parent?: unknown;
};

function stripPercentages(value: string) {
	return value.replace(/%/g, '');
}

function mentionLabelFromId(id: string) {
	return stripPercentages(id).trim();
}

function isUrlEncodedByteToken(id: string) {
	return urlEncodedByteToken.test(id);
}

export function stringContainsMergeFieldToken(value: string) {
	return [...value.matchAll(mergeFieldToken)].some(match => !isUrlEncodedByteToken(match[0]));
}

function addUniqueMention(mentions: MentionAttrs[], mention: MentionAttrs) {
	if (systemGeneratedLinkTokens.has(mention.id)) {
		return;
	}

	if (!mentions.some(existing => existing.id === mention.id)) {
		mentions.push(mention);
	}
}

function decodeHtmlAttribute(value: string) {
	return value
		.replace(/&quot;/g, '"')
		.replace(/&#34;/g, '"')
		.replace(/&#x22;/gi, '"')
		.replace(/&#39;/g, "'")
		.replace(/&#x27;/gi, "'")
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>');
}

function getSpanAttributes(span: string) {
	const attrs: Record<string, string> = {};

	for (const match of span.matchAll(attribute)) {
		attrs[match[1]] = decodeHtmlAttribute(match[3]);
	}

	return attrs;
}

function visitLayout(value: unknown, visitor: (value: unknown, context: VisitContext) => void, context: VisitContext = {}) {
	visitor(value, context);

	if (!value || typeof value !== 'object') {
		return;
	}

	if (Array.isArray(value)) {
		for (const item of value) {
			visitLayout(item, visitor, { parent: value });
		}
		return;
	}

	for (const [key, item] of Object.entries(value)) {
		visitLayout(item, visitor, { key, parent: value });
	}
}

function shouldScanStringForTokens(value: string, context: VisitContext) {
	const parent = context.parent as { type?: unknown } | undefined;

	if (context.key === 'text' && parent?.type === 'text') {
		return false;
	}

	if (context.key === 'contents' && value.includes('<')) {
		return false;
	}

	if (context.key && imageSourceKeys.has(context.key)) {
		return false;
	}

	// presetUrl holds a system-generated link (survey/RSVP, calendar, attachment), never a user-entered merge field.
	if (context.key === 'presetUrl') {
		return false;
	}

	return true;
}

export function getMentionsFromLayoutNodes(layout: unknown): MentionAttrs[] {
	const result: MentionAttrs[] = [];

	visitLayout(layout, value => {
		if (!value || typeof value !== 'object' || Array.isArray(value)) {
			return;
		}

		const node = value as { type?: unknown; attrs?: unknown };
		if (node.type !== 'mention' || !node.attrs || typeof node.attrs !== 'object') {
			return;
		}

		const attrs = node.attrs as { id?: unknown; label?: unknown };
		if (typeof attrs.id === 'string') {
			addUniqueMention(result, {
				id: attrs.id,
				label: typeof attrs.label === 'string' ? attrs.label : mentionLabelFromId(attrs.id)
			});
		}
	});

	return result;
}

export function getMentionsFromDataAttributeStrings(layout: unknown): MentionAttrs[] {
	const result: MentionAttrs[] = [];

	visitLayout(layout, value => {
		if (typeof value !== 'string') {
			return;
		}

		for (const match of value.matchAll(mentionSpan)) {
			const attrs = getSpanAttributes(match[0]);
			const id = attrs['data-id'];
			if (id) {
				addUniqueMention(result, { id, label: attrs['data-label'] ?? mentionLabelFromId(id) });
			}
		}
	});

	return result;
}

export function getMentionsFromTokenStrings(layout: unknown): MentionAttrs[] {
	const result: MentionAttrs[] = [];

	visitLayout(layout, (value, context) => {
		if (typeof value !== 'string') {
			return;
		}

		if (!shouldScanStringForTokens(value, context)) {
			return;
		}

		for (const match of value.matchAll(mergeFieldToken)) {
			const id = match[0];
			if (isUrlEncodedByteToken(id)) {
				continue;
			}

			addUniqueMention(result, { id, label: mentionLabelFromId(id) });
		}
	});

	return result;
}

export function getAllMentionsFromLayout(layout: unknown): MentionAttrs[] {
	const result: MentionAttrs[] = [];

	[
		...getMentionsFromLayoutNodes(layout),
		...getMentionsFromDataAttributeStrings(layout),
		...getMentionsFromTokenStrings(layout)
	].forEach(mention => {
		addUniqueMention(result, mention);
	});

	return result;
}
